import * as React from 'react';

/**
 * Anti-flicker loading gate. Wrap a raw `isLoading` flag so the skeleton it
 * drives behaves like a human expects:
 *
 *  - **No blank pre-frame.** When loading with no data, the skeleton shows
 *    *immediately* (default `delay` 0). The old behaviour deferred the skeleton
 *    by 400ms, which rendered a blank/empty shell first — so the user saw
 *    blank → skeleton → content, which reads as a flash. Showing the skeleton
 *    straight away collapses that to a single transition: skeleton → content.
 *  - **No flicker / strobe on fast loads.** Once `showSkeleton` flips to `true`
 *    it stays `true` for at least `minDuration` ms, so a skeleton that does
 *    appear is on-screen long enough to read instead of blinking.
 *  - **No skeleton at all when data is already here.** Callers gate this on a
 *    flag that is only `true` while there is genuinely nothing to show (e.g.
 *    `isPending && !data`, or an `isInitialLoad`). A refetch that keeps the
 *    previous page visible via TanStack `placeholderData` never trips it.
 *
 * Net behaviour, matching the product directive: *if the data hasn't arrived,
 * show the skeleton for at least 400ms and keep it up until the data comes.*
 *
 * Pass a non-zero `delay` only when you specifically want to suppress the
 * skeleton on sub-`delay` loads (the legacy behaviour); the default no longer
 * does that because it traded a skeleton flash for a worse blank flash.
 *
 * Both timers are cleared on unmount. Reduced-motion users get the same gating
 * (it is timing, not animation) — the skeleton component they pair this with is
 * responsible for honouring `prefers-reduced-motion` on its own shimmer.
 *
 * @param isLoading current raw loading state (e.g. a TanStack Query `isPending`)
 * @param opts.delay       ms to wait before showing the skeleton (default 0)
 * @param opts.minDuration ms to keep the skeleton up once shown (default 400)
 * @returns `showSkeleton` — gate this around your skeleton vs. real content
 *
 * @example
 * const showSkeleton = useSmartLoading(isPending && !data);
 * return showSkeleton ? <TableSkeleton /> : <Table rows={rows} />;
 */
export interface UseSmartLoadingOptions {
  delay?: number;
  minDuration?: number;
}

const DEFAULT_DELAY = 0;
const DEFAULT_MIN_DURATION = 400;

export function useSmartLoading(
  isLoading: boolean,
  opts: UseSmartLoadingOptions = {},
): boolean {
  const { delay = DEFAULT_DELAY, minDuration = DEFAULT_MIN_DURATION } = opts;

  // With no pre-show delay the skeleton must be visible on the very first
  // render where we are loading — otherwise there is a one-frame blank
  // (mount → effect → setState) that reads as a flash. Seeding the initial
  // state from `isLoading` closes that gap.
  const [showSkeleton, setShowSkeleton] = React.useState(
    () => isLoading && delay === 0,
  );

  // Wall-clock time the skeleton became visible, so we can enforce the
  // minimum-on-screen duration when loading finishes.
  const shownAtRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    let delayTimer: ReturnType<typeof setTimeout> | undefined;
    let minTimer: ReturnType<typeof setTimeout> | undefined;

    if (isLoading) {
      if (showSkeleton) {
        // Seeded immediate skeleton (delay 0 at mount): stamp its first-visible
        // time once so the min-duration accounting below subtracts time already
        // on screen instead of holding a fresh full `minDuration`.
        if (shownAtRef.current === null) shownAtRef.current = Date.now();
      } else {
        // Show after `delay` (0 by default — a single macrotask hop; the
        // immediate-on-mount case is already covered by the seeded initial
        // state, so there is no blank frame on first paint). Loads that resolve
        // inside a non-zero `delay` never show a skeleton.
        delayTimer = setTimeout(() => {
          shownAtRef.current = Date.now();
          setShowSkeleton(true);
        }, delay);
      }
    } else if (showSkeleton) {
      // Loading finished while a skeleton is up: keep it visible until it has
      // been on-screen for at least `minDuration`, so it never strobes.
      const shownAt = shownAtRef.current ?? Date.now();
      const remaining = Math.max(0, minDuration - (Date.now() - shownAt));
      minTimer = setTimeout(() => {
        shownAtRef.current = null;
        setShowSkeleton(false);
      }, remaining);
    } else {
      // Loading finished before the skeleton ever appeared — nothing to do.
      shownAtRef.current = null;
    }

    return () => {
      if (delayTimer) clearTimeout(delayTimer);
      if (minTimer) clearTimeout(minTimer);
    };
  }, [isLoading, delay, minDuration, showSkeleton]);

  return showSkeleton;
}
